import React, { createContext, useContext, useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { FlightData, CrewMovementData, Station, STATIONS, CounterDistributionData, TimestampEntry, initialFlightData, NewsMessage, ScannedPassenger, ATSRData, ArrivalStation, ARRIVAL_STATIONS, NotificationData } from '../types';
import { useAuth } from './AuthContext';
import { database } from '../services/firebase';
import { ref, onValue, set, get, update, push, remove, runTransaction } from 'firebase/database';

interface ActiveFlights {
  [slotId: string]: FlightData;
}

const DEFAULT_NOTIFICATION_SOUND = "data:audio/wav;base64,UklGRlIAAABXQVZFZm10IBAAAAABAAEARKwAAIhYAQACABAAZGF0YQQAAAAAAAD/A/8EAf8EAgAEAf8EAf8D/wQCAAQCAAQB/wT/BAIBBP8E/wT/BP8D/wT/BAIEAgT/BP8E/wQCBQEEAgT/A/8E/wQCAAQCAgED/wT/BP8E/wQCBQEE/wP/A/8DAgQEAgT/BAIEAgT/BP8EAgQCAgT/BP8EAgQCAgQCBAD/A/8DAgEE/wP/A/8D/wT/BAIAAQCBQEC/wP/A/8DAgEE/wP/A/8EAgEFAgT/A/8D/wT/A/8D/wP/A/8D/wQCAAQCBQEC/wP/A/8EAgT/A/8D/wQCAAQCAgED/wT/BP8E/wQCAgP/BAIEAgT/BP8EAgQCAgQCBAD/A/8DAgEE/wP/A/8D/wQCAAQCBQEC/wP/A/8DAgEE/wP/A/8EAgEFAgT/A/8D/wT/A/8D/wP/A/8D/wQCAAQCBQEC/wP/A/8EAgT/A/8D/wQCAAQCAgED/wT/BP8E/wQCAgP/BAIEAgT/BP8EAgQCAgQCBAD/A/8DAgEE/wP/A/8D/wQCAAQCBQEC/wP/A/8DAgEE/wP/A/8EAgEFAgT/A/8D/wT/A/8D/wP/A/8D/wQCAAQCBQEC/wP/A/8EAgT/A/8D/wQCAAQCAgED/wT/BP8E/wQCAgP/BAIEAgT/BP8EAgQCAgQCBAD/A/8DAgEE/wP/A/8D/wQCAAQCBQEC/wP/A/8DAgEE/wP/A/8EAgEFAgT/A/8D/wT/A/8D/wP/A/8D/wQCAAQCBQEC/wP/A/8EAgT/A/8D/wQCAAQCAgED/wT/BP8E/wQCAgP/BAIEAgT/BP8EAgQCAgQCBAD/A/8DAgEE/wP/A/8D/wQCAAQCBQEC/wP/A/8DAgEE/wP/A/8EAgEFAgT/A/8D/wT/A/8D/wP/A/8D/wQCAAQCBQEC/wP/A/8EAgT/A/8D/wQCAAQCAgED/wT/BP8E/wQCAgP/BAIEAgT/BP8EAgQCAgQCBAD/A/8DAgEE/wP/A/8D/wQCAAQCBQEC/wP/A/8DAgEE/wP/A/8EAgEFAgT/A/8D/wT/A/8D/wP/A/8D/wQCAAQCBQEC/wP/A/8EAgT/A/8D/wQCAAQCAgED/wT/BP8E/wQCAgP/BAIEAgT/BP8EAgQCAgQCBAD/A/8DAgEE/wP/A/8D/wQCAAQCBQEC/wP/A/8DAgEE/wP/A/8EAgEFAgT/A/8D/wT/A/8D/wP/A/8D/wQCAAQCBQEC/wP/A/8EAgT/A/8D/wQCAAQCAgED/wT/BP8E/wQCAgP/BAIEAgT/BP8EAgQCAgQCBAD/A/8DAgEE/wP/A/8D/wQCAAQCBQEC/wP/A/8DAgEE/wP/A/8EAgEFAgT/A/8D/wT/A/8D/wP/A/8D/wQCAAQCBQEC/wP/A/8EAgT/A/8D/wQCAAQCAgED/wT/BP8E/wQCAgP/BAIE";

interface FlightContextType {
  activeFlights: ActiveFlights;
  selectedFlightSlotId: string | null;
  selectFlight: (slotId: string) => void;
  selectedFlightData: FlightData | null;
  updateCrewMovement: (station: Station, type: 'crew' | 'pilots') => void;
  clearCrewMovement: (station: Station, type: 'crew' | 'pilots') => void;
  updateCounterDistributionField: (section: 'counters' | 'gates' | 'finalFigures' | 'root' | 'booking', key: string, value: string) => void;
  updateAtsrField: (key: string, value: string) => void;
  updateFlightData: (key: keyof FlightData, value: any) => void;
  saveCurrentFlight: (slotId: string) => Promise<{ success: boolean; message: string }>;
  startNewFlightSession: (flightNo: string, flightDate: string) => Promise<void>;
  endFlightSession: (slotId: string) => Promise<void>;
  lastReceived: number;
  isSending: boolean;
  areNotificationsEnabled: boolean;
  toggleNotifications: () => void;
  isAudioUnlocked: boolean;
  newsFeed: NewsMessage[];
  addNewsMessage: (message: string) => Promise<void>;
  clearNewsFeed: () => Promise<void>;
  addScannedPassenger: (passengerData: Omit<ScannedPassenger, 'sequence' | 'scannedAt' | 'scannedBy'>) => void;
  deleteScannedPassenger: (sequence: number) => void;
  addCapturedDoc: (doc: { url: string; capturedAt: string; by: string }) => void;
  showNotification: (message: string, type?: 'success' | 'error' | 'info') => void;
  notification: { message: string, type: 'success' | 'error' | 'info' } | null;
  triggerNewFlightModal: boolean;
  startAndEndSession: (slotId: string) => Promise<void>;
  clearNewFlightModalTrigger: () => void;
  clearSystemTimestamps: () => void;
  clearGateTimestamps: () => void;
  logFinalizeTimestamp: () => void;
  serverTimeOffset: number;
  updateArrivalMovement: (station: ArrivalStation) => void;
  clearArrivalMovement: (station: ArrivalStation) => void;
  revertArrivalMovementToStation: (station: ArrivalStation) => void;
  updateArrivalData: (key: string, value: string) => void;
}

const FlightContext = createContext<FlightContextType | undefined>(undefined);

export const FlightProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [activeFlights, setActiveFlights] = useState<ActiveFlights>({});
  const [selectedFlightSlotId, setSelectedFlightSlotId] = useState<string | null>(() => localStorage.getItem('selectedFlightSlotId'));
  
  const [lastReceived, setLastReceived] = useState(Date.now());
  const [isSending, setIsSending] = useState(false);
  const { user, isMultiFlightEnabled, appAssets } = useAuth();
  const [notification, setNotification] = useState<{ message: string, type: 'success' | 'error' | 'info' } | null>(null);
  const notificationTimer = useRef<number | null>(null);
  const [triggerNewFlightModal, setTriggerNewFlightModal] = useState(false);
  const [serverTimeOffset, setServerTimeOffset] = useState(0);
  const [returnFlights, setReturnFlights] = useState<string[]>([]);
  
  // --- Audio State and Logic ---
  const audioRef = useRef<HTMLAudioElement>(null);
  const audioRef2 = useRef<HTMLAudioElement>(null); // For arrival closed sound
  const [isAudioUnlocked, setIsAudioUnlocked] = useState(false);
  const [soundQueue, setSoundQueue] = useState<number[]>([]);
  const [soundQueue2, setSoundQueue2] = useState<number[]>([]);
  const lastPlayedSoundTimestamp = useRef<number | null>(null);
  const lastPlayedArrivalSoundTimestamp = useRef<number | null>(null);

  const [areNotificationsEnabled, setAreNotificationsEnabled] = useState<boolean>(() => {
    const saved = localStorage.getItem('areNotificationsEnabled');
    return saved !== null ? JSON.parse(saved) : true;
  });
  
  useEffect(() => {
    const offsetRef = ref(database, '.info/serverTimeOffset');
    const unsubscribe = onValue(offsetRef, (snapshot) => {
        setServerTimeOffset(snapshot.val() || 0);
    });

    const returnFlightsRef = ref(database, 'config/returnFlights');
    const returnFlightsUnsubscribe = onValue(returnFlightsRef, (snapshot) => {
        setReturnFlights(snapshot.val() || []);
    });

    return () => {
        unsubscribe();
        returnFlightsUnsubscribe();
    }
  }, []);

  const showNotification = useCallback((message: string, type: 'success' | 'error' | 'info' = 'success') => {
    setNotification({ message, type });
    if (notificationTimer.current) {
      clearTimeout(notificationTimer.current);
    }
    notificationTimer.current = window.setTimeout(() => {
      setNotification(null);
    }, 3000);
  }, []);

  const playSoundNow = useCallback((timestamp: number) => {
    if (audioRef.current && areNotificationsEnabled) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
      audioRef.current.play()
        .then(() => {
          lastPlayedSoundTimestamp.current = timestamp;
        })
        .catch(e => {
          console.error("An unexpected audio error occurred during playback:", e);
          showNotification('Could not play notification sound.', 'error');
        });
    }
  }, [areNotificationsEnabled, showNotification]);

  const playSound = useCallback((timestamp: number) => {
    if (!areNotificationsEnabled) return;

    if (lastPlayedSoundTimestamp.current && Date.now() - lastPlayedSoundTimestamp.current < 1000) {
      return;
    }

    if (isAudioUnlocked) {
      playSoundNow(timestamp);
    } else {
      console.log('Audio has not been unlocked. Queuing sound.');
      setSoundQueue(prev => prev.includes(timestamp) ? prev : [...prev, timestamp]);
    }
  }, [areNotificationsEnabled, isAudioUnlocked, playSoundNow]);

  const playArrivalClosedSoundNow = useCallback((timestamp: number) => {
    if (audioRef2.current && areNotificationsEnabled) {
      audioRef2.current.pause();
      audioRef2.current.currentTime = 0;
      audioRef2.current.play()
        .then(() => {
          lastPlayedArrivalSoundTimestamp.current = timestamp;
        })
        .catch(e => {
          console.error("An unexpected audio error occurred during playback:", e);
          showNotification('Could not play arrival closed sound.', 'error');
        });
    }
}, [areNotificationsEnabled, showNotification]);

  const playArrivalClosedSound = useCallback((timestamp: number) => {
      if (!areNotificationsEnabled) return;
  
      if (lastPlayedArrivalSoundTimestamp.current && Date.now() - lastPlayedArrivalSoundTimestamp.current < 1000) {
        return;
      }
  
      if (isAudioUnlocked) {
        playArrivalClosedSoundNow(timestamp);
      } else {
        console.log('Audio has not been unlocked. Queuing arrival closed sound.');
        setSoundQueue2(prev => prev.includes(timestamp) ? prev : [...prev, timestamp]);
      }
  }, [areNotificationsEnabled, isAudioUnlocked, playArrivalClosedSoundNow]);


  useEffect(() => {
    const unlockAudio = () => {
      if (isAudioUnlocked || (!audioRef.current && !audioRef2.current)) {
        window.removeEventListener('pointerdown', unlockAudio);
        window.removeEventListener('keydown', unlockAudio);
        return;
      }
      
      console.log("Interaction detected, attempting to unlock audio...");
      const promises = [];
      if (audioRef.current) {
          audioRef.current.muted = true;
          promises.push(audioRef.current.play());
      }
      if (audioRef2.current) {
          audioRef2.current.muted = true;
          promises.push(audioRef2.current.play());
      }

      Promise.all(promises).then(() => {
        if(audioRef.current) {
            audioRef.current.pause();
            audioRef.current.currentTime = 0;
            audioRef.current.muted = false;
        }
        if(audioRef2.current) {
            audioRef2.current.pause();
            audioRef2.current.currentTime = 0;
            audioRef2.current.muted = false;
        }
        setIsAudioUnlocked(true);
        console.log("Audio contexts unlocked successfully!");
        window.removeEventListener('pointerdown', unlockAudio);
        window.removeEventListener('keydown', unlockAudio);
      }).catch(error => {
        console.warn('Audio unlock attempt failed. Will retry on next user interaction.', error);
      });
    };

    window.addEventListener('pointerdown', unlockAudio);
    window.addEventListener('keydown', unlockAudio);

    return () => {
      window.removeEventListener('pointerdown', unlockAudio);
      window.removeEventListener('keydown', unlockAudio);
    };
  }, [isAudioUnlocked]);
  
  useEffect(() => {
    if (isAudioUnlocked && soundQueue.length > 0) {
      const latestTimestamp = soundQueue[soundQueue.length - 1];
      playSoundNow(latestTimestamp);
      setSoundQueue([]);
    }
  }, [isAudioUnlocked, soundQueue, playSoundNow]);

  useEffect(() => {
    if (isAudioUnlocked && soundQueue2.length > 0) {
      const latestTimestamp = soundQueue2[soundQueue2.length - 1];
      playArrivalClosedSoundNow(latestTimestamp);
      setSoundQueue2([]);
    }
  }, [isAudioUnlocked, soundQueue2, playArrivalClosedSoundNow]);


  const writeToDb = useCallback(async (writeFn: () => Promise<any>) => {
    setIsSending(true);
    try {
        await writeFn();
    } catch (error) {
        console.error("DB write error:", error);
        showNotification('An error occurred while saving data.', 'error');
        throw error;
    } finally {
        setIsSending(false);
    }
  }, [showNotification]);

  const selectFlight = useCallback((slotId: string) => {
    if (slotId) {
      localStorage.setItem('selectedFlightSlotId', slotId);
      setSelectedFlightSlotId(slotId);
    } else {
      localStorage.removeItem('selectedFlightSlotId');
      setSelectedFlightSlotId(null);
    }
  }, []);

  useEffect(() => {
    const activeFlightsRef = ref(database, 'activeFlights');
    const flightUnsubscribe = onValue(activeFlightsRef, (snapshot) => {
        const data = snapshot.val();
        setActiveFlights(data || {});
        
        const currentSlotId = localStorage.getItem('selectedFlightSlotId');
        if (data) {
            if (!currentSlotId || !data[currentSlotId]) {
                const firstSlotId = Object.keys(data)[0];
                selectFlight(firstSlotId || '');
            }
            
            const selectedData = currentSlotId ? data[currentSlotId] : null;
            if (selectedData && selectedData.lastNotification) {
                const remoteNotification = selectedData.lastNotification as NotificationData;
                
                const isNewNotification = remoteNotification.timestamp !== lastPlayedSoundTimestamp.current && 
                                          remoteNotification.timestamp !== lastPlayedArrivalSoundTimestamp.current;

                if (isNewNotification && remoteNotification.triggeredBy !== user?.username) {
                    if (remoteNotification.soundType === 'arrivalClosed') {
                        playArrivalClosedSound(remoteNotification.timestamp);
                    } else {
                        playSound(remoteNotification.timestamp);
                    }
                }
            }

        } else {
            selectFlight('');
        }
        
        setLastReceived(Date.now());
    });

    return () => flightUnsubscribe();
  }, [selectFlight, user?.username, playSound, playArrivalClosedSound]);

  const selectedFlightData = useMemo(() => {
    if (selectedFlightSlotId && activeFlights[selectedFlightSlotId]) {
      const flightData = activeFlights[selectedFlightSlotId];
      return {
        ...initialFlightData,
        ...flightData,
        crewMovement: flightData.crewMovement || {},
        counterDistribution: {
          ...initialFlightData.counterDistribution,
          ...(flightData.counterDistribution || {}),
          booking: { ...initialFlightData.counterDistribution.booking, ...(flightData.counterDistribution?.booking || {}) },
          counters: { ...initialFlightData.counterDistribution.counters, ...(flightData.counterDistribution?.counters || {}) },
          gates: { ...initialFlightData.counterDistribution.gates, ...(flightData.counterDistribution?.gates || {}) },
          finalFigures: { ...initialFlightData.counterDistribution.finalFigures, ...(flightData.counterDistribution?.finalFigures || {}) },
        },
        atsr: { ...initialFlightData.atsr, ...(flightData.atsr || {}) },
        arrivalMovement: {
            ...initialFlightData.arrivalMovement,
            ...(flightData.arrivalMovement || {})
        },
        capturedDocs: flightData.capturedDocs || [],
        scannedPassengers: flightData.scannedPassengers || [],
        newsFeed: flightData.newsFeed || {},
        lastNotification: flightData.lastNotification || null,
        finalizeTimestamp: flightData.finalizeTimestamp || null,
      };
    }
    
    return null;
  }, [activeFlights, selectedFlightSlotId]);

  const newsFeed = useMemo((): NewsMessage[] => {
      if (!selectedFlightData || !selectedFlightData.newsFeed) return [];
      const newsFeedObj = selectedFlightData.newsFeed;
      
      return Object.entries(newsFeedObj)
          .filter((entry): entry is [string, Omit<NewsMessage, 'id'>] => {
            const value = entry[1];
            return typeof value === 'object' && value !== null;
          })
          .map(([id, value]) => ({ id, ...value }))
          .sort((a, b) => a.timestamp - b.timestamp);
  }, [selectedFlightData]);

  const toggleNotifications = useCallback(() => {
    setAreNotificationsEnabled(prev => {
        const newState = !prev;
        localStorage.setItem('areNotificationsEnabled', JSON.stringify(newState));
        return newState;
    });
  }, []);

  const updateCrewMovement = useCallback((station: Station, type: 'crew' | 'pilots') => {
    if (!selectedFlightSlotId) return;
    const arrivalTime = new Date();
    
    // Play sound locally immediately for responsiveness
    playSound(Date.now());
    
    writeToDb(() => runTransaction(ref(database, `activeFlights/${selectedFlightSlotId}`), (currentData) => {
      if (currentData === null) return;

      const newEntry: Omit<TimestampEntry, 'modified'> = {
        time: arrivalTime.toISOString(),
        by: user?.username || 'Unknown',
      };

      const updateStation = (data: FlightData, updateType: 'crew' | 'pilots') => {
        const crewMovement = data.crewMovement || {};
        const stationTimestamps = crewMovement[station] || { crew: [], pilots: [] };
        const existingTimestamps = stationTimestamps[updateType] || [];
        
        const entryToAdd: TimestampEntry = { ...newEntry, modified: existingTimestamps.length > 0 };

        const updatedTimestamps = {
          ...stationTimestamps,
          [updateType]: [...existingTimestamps, entryToAdd],
        };
        
        data.crewMovement = { ...crewMovement, [station]: updatedTimestamps };
      };

      if (currentData.crewAndPilotsArrivedTogether) {
        updateStation(currentData, 'crew');
        updateStation(currentData, 'pilots');
      } else {
        updateStation(currentData, type);
      }
      
      const notificationStations: Station[] = ['Crew Arrived', '1st X-ray', '1st Pax IN', 'Doors Closed'];
      if (notificationStations.includes(station)) {
          currentData.lastNotification = {
              station: station,
              time: arrivalTime.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' }),
              timestamp: Date.now(),
              triggeredBy: user?.username || 'Unknown',
          };
      }
      
      return currentData;
    }));
  }, [user, writeToDb, selectedFlightSlotId, playSound]);

  const clearCrewMovement = useCallback((station: Station, type: 'crew' | 'pilots') => {
    if (!selectedFlightSlotId) return;

    writeToDb(() => runTransaction(ref(database, `activeFlights/${selectedFlightSlotId}/crewMovement/${station}`), (stationData) => {
        if (stationData && stationData[type] && Array.isArray(stationData[type]) && stationData[type].length > 0) {
            stationData[type].pop(); // Remove the last entry
        }
        return stationData;
    }));
  }, [writeToDb, selectedFlightSlotId]);

  const updateCounterDistributionField = useCallback((section: 'counters' | 'gates' | 'finalFigures' | 'root' | 'booking', key: string, value: string) => {
    if (!selectedFlightSlotId) return;
    const updates: { [key: string]: any } = {};
    if (section === 'root') {
      updates[`/activeFlights/${selectedFlightSlotId}/counterDistribution/${key}`] = value;
    } else {
      updates[`/activeFlights/${selectedFlightSlotId}/counterDistribution/${section}/${key}`] = value;
    }
    writeToDb(() => update(ref(database), updates));
  }, [writeToDb, selectedFlightSlotId]);
  
  const updateAtsrField = useCallback((key: string, value: string) => {
    if (!selectedFlightSlotId) return;
    const updates: { [path: string]: any } = {};
    updates[`/activeFlights/${selectedFlightSlotId}/atsr/${key}`] = value;
    writeToDb(() => update(ref(database), updates));
  }, [writeToDb, selectedFlightSlotId]);
  
  const updateFlightData = useCallback((key: keyof FlightData, value: any) => {
    if (!selectedFlightSlotId) return;
    const updates: { [key: string]: any } = {};
    updates[`/activeFlights/${selectedFlightSlotId}/${key}`] = value;
    writeToDb(() => update(ref(database), updates));
  }, [writeToDb, selectedFlightSlotId]);

  const updateArrivalMovement = useCallback((station: ArrivalStation) => {
    if (!selectedFlightSlotId || !user) return;
    const arrivalTime = new Date();

    if (station === 'Arrival Closed') {
        playArrivalClosedSound(Date.now());
    }
    
    writeToDb(() => runTransaction(ref(database, `activeFlights/${selectedFlightSlotId}`), (currentData) => {
        if (currentData === null) return;

        const arrivalMovement = currentData.arrivalMovement || {};
        const stationData = arrivalMovement[station] || { events: [] };
        
        const newEntry: TimestampEntry = {
            time: arrivalTime.toISOString(),
            by: user.username,
            modified: stationData.events && stationData.events.length > 0,
        };
        
        stationData.events = [...(stationData.events || []), newEntry];
        currentData.arrivalMovement = { ...arrivalMovement, [station]: stationData };

        if (station === 'Arrival Closed') {
            currentData.lastNotification = {
              station: station,
              time: arrivalTime.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' }),
              timestamp: Date.now(),
              triggeredBy: user.username,
              soundType: 'arrivalClosed',
            };
        }
        
        return currentData;
    }));
  }, [user, writeToDb, selectedFlightSlotId, playArrivalClosedSound]);

  const clearArrivalMovement = useCallback((station: ArrivalStation) => {
    if (!selectedFlightSlotId) return;

    writeToDb(() => runTransaction(ref(database, `activeFlights/${selectedFlightSlotId}/arrivalMovement/${station}`), (stationData) => {
        if (stationData && stationData.events && Array.isArray(stationData.events) && stationData.events.length > 0) {
            stationData.events.pop(); // Remove the last entry
        }
        return stationData;
    }));
  }, [writeToDb, selectedFlightSlotId]);

  const revertArrivalMovementToStation = useCallback((revertToStation: ArrivalStation) => {
    if (!selectedFlightSlotId) return;

    writeToDb(() => runTransaction(ref(database, `activeFlights/${selectedFlightSlotId}/arrivalMovement`), (arrivalMovement) => {
        if (!arrivalMovement) return arrivalMovement;

        const revertToIndex = ARRIVAL_STATIONS.indexOf(revertToStation);
        if (revertToIndex === -1) return arrivalMovement;

        // Iterate through stations *after* the one we're reverting to
        for (let i = revertToIndex + 1; i < ARRIVAL_STATIONS.length; i++) {
            const stationToClear = ARRIVAL_STATIONS[i];
            if (arrivalMovement[stationToClear]) {
                // Delete the station data to clear its timestamps
                delete arrivalMovement[stationToClear];
            }
        }

        return arrivalMovement;
    }));
  }, [writeToDb, selectedFlightSlotId]);

  const updateArrivalData = useCallback((key: string, value: string) => {
    if (!selectedFlightSlotId) return;
    writeToDb(() => 
        set(ref(database, `activeFlights/${selectedFlightSlotId}/arrivalMovement/${key}`), value)
    );
  }, [writeToDb, selectedFlightSlotId]);

  const saveCurrentFlight = useCallback(async (slotId: string): Promise<{ success: boolean; message: string }> => {
    const flightToSave = activeFlights[slotId];
    if (!flightToSave || !flightToSave.counterDistribution.flightNo) {
      return { success: false, message: "Cannot create report: Flight Number is missing." };
    }

    const { flightNo } = flightToSave.counterDistribution;
    const now = new Date();
    const flightDate = now.toISOString().split('T')[0];
    const flightId = `${flightNo}-${now.getTime()}`;
    const record = { ...flightToSave, id: flightId, flightDate };
    
    try {
        await writeToDb(() => set(ref(database, `flightRecords/${flightId}`), record));
        return { success: true, message: `Report for flight ${flightNo} created successfully.` };
    } catch (error) {
        console.error("Error saving flight record:", error);
        return { success: false, message: "An error occurred while saving the report." };
    }
  }, [activeFlights, writeToDb]);

  const endFlightSession = useCallback(async (slotId: string): Promise<void> => {
    try {
      const result = await saveCurrentFlight(slotId);
      if (result.success) {
        await writeToDb(() => remove(ref(database, `activeFlights/${slotId}`)));
        showNotification(result.message, 'success');
      } else {
        showNotification(result.message, 'error');
      }
    } catch (error) {
      console.error("Error ending flight session:", error);
      showNotification("An error occurred while ending the session.", 'error');
      throw error;
    }
  }, [saveCurrentFlight, writeToDb, showNotification]);
  
  const startNewFlightSession = useCallback(async (flightNo: string, flightDate: string): Promise<void> => {
    const existingSlots = Object.keys(activeFlights);
    const maxFlights = isMultiFlightEnabled ? 3 : 1;
    if (existingSlots.length >= maxFlights) {
      showNotification(`Cannot start new flight: Maximum of ${maxFlights} active flight(s) reached.`, "error");
      return;
    }

    let newSlotId: string | null = null;
    if (isMultiFlightEnabled) {
        for (let i = 0; i < 3; i++) {
            const potentialSlotId = `slot_${i}`;
            if (!existingSlots.includes(potentialSlotId)) {
                newSlotId = potentialSlotId;
                break;
            }
        }
    } else {
        newSlotId = 'slot_0';
    }

    if (!newSlotId) {
        showNotification("Error finding an available flight slot.", "error");
        return;
    }

    const newFlightState: FlightData = {
      ...initialFlightData,
      isReturnFlight: returnFlights.includes(flightNo),
      scannedPassengers: [],
      counterDistribution: {
        ...initialFlightData.counterDistribution,
        flightNo: flightNo,
        etd: `${flightDate}T00:00`,
      },
    };

    try {
      await writeToDb(() => set(ref(database, `activeFlights/${newSlotId}`), newFlightState));
      selectFlight(newSlotId);
    } catch (error) {
      console.error("Error starting new flight report:", error);
      throw error;
    }
  }, [writeToDb, activeFlights, showNotification, selectFlight, isMultiFlightEnabled, returnFlights]);

  const addNewsMessage = useCallback(async (message: string): Promise<void> => {
    if (!message.trim() || !user || !selectedFlightSlotId) return;
    const newMessage: Omit<NewsMessage, 'id'> = {
        text: message.trim(),
        sender: user.username,
        timestamp: Date.now()
    };
    await writeToDb(() => push(ref(database, `activeFlights/${selectedFlightSlotId}/newsFeed`), newMessage));
  }, [user, writeToDb, selectedFlightSlotId]);

  const clearNewsFeed = useCallback(async (): Promise<void> => {
    if (!selectedFlightSlotId) return;
    await writeToDb(() => remove(ref(database, `activeFlights/${selectedFlightSlotId}/newsFeed`)));
  }, [writeToDb, selectedFlightSlotId]);

  const addCapturedDoc = useCallback((doc: { url: string; capturedAt: string; by: string }) => {
    if (!selectedFlightSlotId) return;
    const docsRef = ref(database, `activeFlights/${selectedFlightSlotId}/capturedDocs`);
    writeToDb(() => runTransaction(docsRef, (currentDocs) => {
      if (currentDocs === null) {
        currentDocs = [];
      }
      currentDocs.push(doc);
      return currentDocs;
    }));
  }, [writeToDb, selectedFlightSlotId]);

  const addScannedPassenger = useCallback((passengerData: Omit<ScannedPassenger, 'sequence' | 'scannedAt' | 'scannedBy'>) => {
    if (!user || !selectedFlightSlotId) return;

    const passengersRef = ref(database, `activeFlights/${selectedFlightSlotId}/scannedPassengers`);
    writeToDb(() => runTransaction(passengersRef, (currentPassengers) => {
      if (currentPassengers === null) {
        currentPassengers = [];
      }
      const newPassenger: ScannedPassenger = {
        ...passengerData,
        sequence: currentPassengers.length + 1,
        scannedAt: new Date().toISOString(),
        scannedBy: user.username,
      };
      currentPassengers.push(newPassenger);
      return currentPassengers;
    }));
  }, [user, writeToDb, selectedFlightSlotId]);
  
  const deleteScannedPassenger = useCallback((sequence: number) => {
    if (!selectedFlightSlotId) return;

    const passengersRef = ref(database, `activeFlights/${selectedFlightSlotId}/scannedPassengers`);
    writeToDb(() => runTransaction(passengersRef, (currentPassengers) => {
        if (currentPassengers === null || !Array.isArray(currentPassengers)) {
            return currentPassengers;
        }
        // Filter out the passenger and re-sequence
        const updatedPassengers = currentPassengers
            .filter((p: ScannedPassenger) => p.sequence !== sequence)
            .map((p: ScannedPassenger, index: number) => ({
                ...p,
                sequence: index + 1
            }));
        
        return updatedPassengers;
    }));
  }, [writeToDb, selectedFlightSlotId]);

  const startAndEndSession = useCallback(async (slotId: string): Promise<void> => {
    await endFlightSession(slotId);
    setTriggerNewFlightModal(true);
  }, [endFlightSession]);

  const clearNewFlightModalTrigger = useCallback(() => {
    setTriggerNewFlightModal(false);
  }, []);

  const clearSystemTimestamps = useCallback(() => {
    if (!selectedFlightSlotId) return;
    writeToDb(() => runTransaction(ref(database, `activeFlights/${selectedFlightSlotId}/crewMovement`), () => {
        return {};
    }));
    showNotification('All crew and pilot timestamps have been cleared.', 'success');
  }, [writeToDb, selectedFlightSlotId, showNotification]);

  const clearGateTimestamps = useCallback(() => {
    if (!selectedFlightSlotId) return;
    const gateAndAfterStations = STATIONS.slice(STATIONS.indexOf('At Gate'));

    writeToDb(() => runTransaction(ref(database, `activeFlights/${selectedFlightSlotId}/crewMovement`), (crewMovementData) => {
        if (crewMovementData) {
            gateAndAfterStations.forEach(station => {
                if (crewMovementData[station]) {
                    delete crewMovementData[station];
                }
            });
        }
        return crewMovementData;
    }));
    showNotification('All timestamps from "At Gate" onwards have been cleared.', 'success');
  }, [writeToDb, selectedFlightSlotId, showNotification]);

  const logFinalizeTimestamp = useCallback(() => {
    if (!selectedFlightSlotId || !user) return;
    const finalizeTime = new Date();
    const flightData = activeFlights[selectedFlightSlotId];
    if (!flightData) return;

    const newEntry: TimestampEntry = {
        time: finalizeTime.toISOString(),
        by: user.username,
        modified: !!flightData.finalizeTimestamp,
    };
    writeToDb(() => 
        set(ref(database, `activeFlights/${selectedFlightSlotId}/finalizeTimestamp`), newEntry)
    );
    showNotification('Flight finalized timestamp recorded.');
  }, [selectedFlightSlotId, user, activeFlights, writeToDb, showNotification]);


  const contextValue = useMemo(() => ({
    activeFlights,
    selectedFlightSlotId,
    selectFlight,
    selectedFlightData,
    updateCrewMovement,
    clearCrewMovement,
    updateCounterDistributionField,
    updateAtsrField,
    updateFlightData,
    saveCurrentFlight,
    startNewFlightSession,
    endFlightSession,
    lastReceived,
    isSending,
    areNotificationsEnabled,
    toggleNotifications,
    isAudioUnlocked,
    newsFeed,
    addNewsMessage,
    clearNewsFeed,
    addScannedPassenger,
    deleteScannedPassenger,
    addCapturedDoc,
    notification,
    showNotification,
    triggerNewFlightModal,
    startAndEndSession,
    clearNewFlightModalTrigger,
    clearSystemTimestamps,
    clearGateTimestamps,
    logFinalizeTimestamp,
    serverTimeOffset,
    updateArrivalMovement,
    clearArrivalMovement,
    revertArrivalMovementToStation,
    updateArrivalData,
  }), [
    activeFlights,
    selectedFlightSlotId,
    selectFlight,
    selectedFlightData,
    updateCrewMovement,
    clearCrewMovement,
    updateCounterDistributionField,
    updateAtsrField,
    updateFlightData,
    saveCurrentFlight,
    startNewFlightSession,
    endFlightSession,
    lastReceived,
    isSending,
    areNotificationsEnabled,
    toggleNotifications,
    isAudioUnlocked,
    newsFeed,
    addNewsMessage,
    clearNewsFeed,
    addScannedPassenger,
    deleteScannedPassenger,
    addCapturedDoc,
    notification,
    showNotification,
    triggerNewFlightModal,
    startAndEndSession,
    clearNewFlightModalTrigger,
    clearSystemTimestamps,
    clearGateTimestamps,
    logFinalizeTimestamp,
    serverTimeOffset,
    updateArrivalMovement,
    clearArrivalMovement,
    revertArrivalMovementToStation,
    updateArrivalData,
  ]);

  return (
    <FlightContext.Provider value={contextValue}>
      <>
        {children}
        <audio
            ref={audioRef}
            src={appAssets.notificationSound || DEFAULT_NOTIFICATION_SOUND}
            key={appAssets.notificationSound}
        />
        <audio
            ref={audioRef2}
            src={appAssets.notificationSound2 || appAssets.notificationSound || DEFAULT_NOTIFICATION_SOUND}
            key={appAssets.notificationSound2}
        />
      </>
    </FlightContext.Provider>
  );
};

export const useFlight = (): FlightContextType => {
  const context = useContext(FlightContext);
  if (context === undefined) {
    throw new Error('useFlight must be used within a FlightProvider');
  }
  return context;
};
